import React, { useMemo } from "react";
import { gql, useQuery } from "urql";
import { Link, useParams, useLocation } from "wouter";

import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";

import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { SelectColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";
import { Panel } from "../../../../components/src/common/Panel";
import { CustomerForm } from "./CustomerForm";
import { Customer } from "../../types.generated";

const CUSTOMERS = gql`
    query customers {
        customers {
            _id
            clientIds
            clientNumber
            clientAccount
            clientAccountLabel
            organizationName
            address
            postalCode
            city
            country
            emails
            swedishFinancialAuthorityCategory
            swedishFinancialAuthorityCategoryString
            updateUserId
            updateUserInfo {
                name
            }
            updateTimestamp
        }
    }
`;

export const CustomersPage = (): React.ReactElement => {
    const params: any = useParams<{ id: string }>();
    const [, navigate] = useLocation();

    const [{ fetching, data, error }] = useQuery<{ customers?: Partial<Customer>[] }>({
        query: CUSTOMERS
    });

    const { id } = params;

    const columns = useMemo<ReactTableColumnDef<Customer>[]>(() => {
        const columnHelper = createReactTableColumnHelper<Customer>();

        return [
            columnHelper.accessor("_id", {
                header: "Id",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <Link to={`/backoffice/customers/${value}`}>#</Link>;
                },
                size: 50
            }),
            columnHelper.accessor("clientNumber", {
                header: "clientNumber",
                size: 120
            }),
            columnHelper.accessor("clientAccount", {
                header: "clientAccount",
                size: 180
            }),
            columnHelper.accessor("organizationName", {
                header: "Organization name",
                size: 220
            }),
            columnHelper.accessor("clientAccountLabel", {
                header: "clientAccountLabel",
                size: 160
            }),
            columnHelper.accessor("address", {
                header: "Address",
                size: 180
            }),
            columnHelper.accessor("postalCode", {
                header: "Postal code",
                size: 100
            }),
            columnHelper.accessor("emails", {
                header: "Emails",
                size: 220
            }),
            columnHelper.accessor("swedishFinancialAuthorityCategory", {
                header: "Swedish financial authority category",
                filter: SelectColumnFilter,
                size: 250
            }),
            columnHelper.accessor((row) => row.updateUserInfo?.name ?? "", {
                id: "updateUserInfo.name",
                header: "Updated by",
                cell: (cellProps) => cellProps.getValue(),
                size: 150
            }),
            columnHelper.accessor("updateTimestamp", {
                header: "Update timestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value != null ? String(value) : ""}</div>;
                },
                size: 180
            })
        ];
    }, []);

    if (fetching) return <div>Loading...</div>;

    if (error) {
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    }

    if (data.customers === null || data.customers.length === 0) return <div>No documents</div>;

    return (
        <div>
            <div>Number of documents: {data.customers.length}</div>
            <Link to={"/backoffice/customers/new"}>New customer</Link>
            <ReactTable columns={columns} data={data.customers} defaultHiddenColumns={[]} initialPageSize={100} />
            <Panel
                isOpen={!!id}
                onDismiss={() => navigate(`/backoffice/customers`)}
                title={id !== "new" ? "Edit Customer" : "New Customer"}
            >
                <CustomerForm id={id} />
            </Panel>
        </div>
    );
};
